import type {
    CommerceAddress,
    CommerceAddressRegion,
    BeginCommerceQuickBuyRequest,
    CommerceCart,
    CommerceCheckout,
    CommerceCheckoutChangeReason,
    CommerceCheckoutChangedEvent,
    CommerceCheckoutChangedListener,
    CommerceCheckoutContact,
    CommerceCheckoutPaymentConfiguration,
    CommerceCheckoutDomain,
    CommerceEventUnsubscribe,
    CommerceOperationOptions,
    CommerceRecurringFrequencyOption,
    CompleteCommerceCheckoutResponse,
    SubmitCommerceCheckoutResponse,
} from "@teqtank/commerce-contracts";
import { DisposableListenerRegistry } from "../events/disposable-listener-registry.js";
import { CommerceCartService } from "../cart/commerce-cart-service.js";
import { CommerceError } from "../errors/commerce-error.js";
import { CommerceCheckoutRepository } from "./commerce-checkout-repository.js";
import {
    normalizeCheckoutAddress,
    normalizeCheckoutContact,
    requireCheckoutIdentifier,
} from "./checkout-request-normalizer.js";

export class CommerceCheckoutService implements CommerceCheckoutDomain {
    private readonly listeners = new DisposableListenerRegistry<CommerceCheckoutChangedEvent>();
    private currentCheckout: CommerceCheckout | undefined;
    private quickBuyActive = false;

    public constructor(
        private readonly repository: CommerceCheckoutRepository,
        private readonly cart: CommerceCartService,
    ) {}
    public getAddressRegions(
        countryCode: string,
        options: CommerceOperationOptions = {},
    ): Promise<readonly CommerceAddressRegion[]> {
        const normalized = countryCode.trim().toUpperCase();
        if (!/^[A-Z]{2}$/u.test(normalized)) {
            throw new TypeError("Country code must contain two letters.");
        }
        return this.repository.getAddressRegions(normalized, options);
    }

    public async beginQuickBuy(
        request: BeginCommerceQuickBuyRequest,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        const checkout = await this.repository.beginQuickBuy(request, options);
        this.quickBuyActive = true;
        return this.accept(checkout, "quick-buy-begun");
    }

    public async begin(options: CommerceOperationOptions = {}): Promise<CommerceCheckout> {
        const cart = await this.cart.get(options);
        return this.beginForCart(cart, options);
    }
    public async beginForCart(
        cart: CommerceCart,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        if (cart.lines.length === 0)
            throw new CommerceError(
                "checkout-empty-cart",
                "Checkout requires at least one Cart line.",
            );
        const checkout = await this.repository.begin(options);
        this.quickBuyActive = false;
        return this.accept(checkout, "begun");
    }
    public async get(options: CommerceOperationOptions = {}): Promise<CommerceCheckout | null> {
        const checkout = await this.repository.get(options);
        if (checkout === null) {
            this.invalidate();
            return null;
        }
        this.quickBuyActive = false;
        return this.accept(checkout, "loaded");
    }
    public async clearCustomerDetails(
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        return this.cart.coordinateMutation(async () =>
            this.accept(
                await this.repository.clearCustomerDetails(this.currentCheckout?.version, options),
                "customer-details-cleared",
            ),
        );
    }

    public async updateContact(
        contact: CommerceCheckoutContact,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        return this.cart.coordinateMutation(async () =>
            this.accept(
                await this.repository.updateContact(
                    normalizeCheckoutContact(contact),
                    this.currentCheckout?.version,
                    options,
                ),
                "contact-updated",
            ),
        );
    }
    public async updateShippingAddress(
        address: CommerceAddress,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        return this.cart.coordinateMutation(async () =>
            this.accept(
                await this.repository.updateAddress(
                    normalizeCheckoutAddress(address),
                    "shipping",
                    true,
                    this.currentCheckout?.version,
                    options,
                ),
                "address-updated",
            ),
        );
    }
    public async updateBillingAddress(
        address: CommerceAddress,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        return this.cart.coordinateMutation(async () =>
            this.accept(
                await this.repository.updateAddress(
                    normalizeCheckoutAddress(address),
                    "billing",
                    false,
                    this.currentCheckout?.version,
                    options,
                ),
                "address-updated",
            ),
        );
    }
    public async useShippingAddressForBilling(
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        const address = this.requireCurrent().shippingAddress;
        if (address === null)
            throw new CommerceError(
                "checkout-address-invalid",
                "A shipping address is required first.",
            );
        return this.cart.coordinateMutation(async () =>
            this.accept(
                await this.repository.updateAddress(
                    address,
                    "shipping",
                    true,
                    this.currentCheckout?.version,
                    options,
                ),
                "address-updated",
            ),
        );
    }
    public async selectShippingMethod(
        shippingMethodId: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        return this.cart.coordinateMutation(async () =>
            this.accept(
                await this.repository.selectShipping(
                    requireCheckoutIdentifier(shippingMethodId, "Shipping method ID"),
                    this.currentCheckout?.version,
                    options,
                ),
                "shipping-selected",
            ),
        );
    }
    public async applyCoupon(
        code: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        const normalizedCode = code.trim();
        await this.cart.applyCoupon(normalizedCode, options);
        try {
            const calculated = await this.repository.calculate(
                this.currentCheckout?.version,
                false,
                options,
            );
            const accepted = calculated.cart.coupons.some(
                (coupon) => coupon.code.toUpperCase() === normalizedCode.toUpperCase(),
            );
            if (!accepted) {
                const message =
                    calculated.validation.errors[0]?.message ?? "The coupon code was not accepted.";
                throw new CommerceError("cart-coupon-invalid", message);
            }
            return this.accept(calculated, "coupon-applied");
        } catch (error) {
            await this.cart.removeCoupon(normalizedCode, options);
            try {
                this.accept(
                    await this.repository.calculate(this.currentCheckout?.version, false, options),
                    "calculated",
                );
            } catch {
                // Preserve the original coupon failure; the previous authoritative
                // checkout remains the safest view when rollback recalculation fails.
            }
            throw error;
        }
    }
    public async removeCoupon(
        code: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        await this.cart.removeCoupon(code, options);
        return this.accept(
            await this.repository.calculate(this.currentCheckout?.version, false, options),
            "coupon-removed",
        );
    }
    public async applyCustomer(options: CommerceOperationOptions = {}): Promise<CommerceCheckout> {
        return this.cart.coordinateMutation(async () =>
            this.accept(
                await this.repository.applyCustomer(this.currentCheckout?.version, options),
                "customer-applied",
            ),
        );
    }
    public async selectSavedPaymentMethod(
        paymentMethodId: string | null,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        return this.cart.coordinateMutation(async () =>
            this.accept(
                await this.repository.selectSavedPaymentMethod(
                    paymentMethodId,
                    this.currentCheckout?.version,
                    options,
                ),
                "payment-method-selected",
            ),
        );
    }
    public getPaymentConfiguration(
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckoutPaymentConfiguration> {
        return this.repository.getPaymentConfiguration(options);
    }
    public async selectPaymentOption(
        paymentOptionId: string | null,
        savedPaymentMethodId: string | null = null,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckoutPaymentConfiguration> {
        return this.cart.coordinateMutation(async () => {
            const response = await this.repository.selectPaymentOption(
                paymentOptionId,
                savedPaymentMethodId,
                this.currentCheckout?.version,
                options,
            );
            this.accept(response.checkout, "payment-option-selected");
            return response.payment;
        });
    }
    public async applyPoints(
        apply: boolean,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckoutPaymentConfiguration> {
        return this.cart.coordinateMutation(async () => {
            const response = await this.repository.applyPoints(
                apply,
                this.currentCheckout?.version,
                options,
            );
            this.accept(response.checkout, "points-applied");
            return response.payment;
        });
    }
    public async calculate(options: CommerceOperationOptions = {}): Promise<CommerceCheckout> {
        return this.cart.coordinateMutation(async () =>
            this.accept(
                await this.repository.calculate(this.currentCheckout?.version, false, options),
                "calculated",
            ),
        );
    }
    public async bypassAddressValidation(
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        return this.cart.coordinateMutation(async () =>
            this.accept(
                await this.repository.calculate(this.currentCheckout?.version, true, options),
                "calculated",
            ),
        );
    }
    public async respondToUpsell(
        upsellId: string,
        accepted: boolean,
        choiceId?: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        return this.cart.coordinateMutation(async () => {
            const checkout = this.accept(
                await this.repository.respondToUpsell(
                    upsellId,
                    accepted,
                    choiceId,
                    this.currentCheckout?.version,
                    options,
                ),
                "upsell-responded",
            );
            if (accepted) await this.cart.adoptCheckout(checkout.cart);
            return checkout;
        });
    }
    public async updateItemPurchase(
        lineId: string,
        purchaseType: "one-time" | "recurring",
        frequency?: number | CommerceRecurringFrequencyOption,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCheckout> {
        const frequencyId = typeof frequency === "object" ? frequency.id : undefined;
        const frequencyDays = typeof frequency === "number" ? frequency : frequency?.days;
        return this.cart.coordinateMutation(async () => {
            const checkout = this.accept(
                await this.repository.updateItemPurchase(
                    lineId,
                    purchaseType,
                    frequencyId,
                    frequencyDays,
                    this.currentCheckout?.version,
                    options,
                ),
                "purchase-updated",
            );
            await this.cart.adoptCheckout(checkout.cart);
            return checkout;
        });
    }
    public async submit(_options: CommerceOperationOptions = {}): Promise<CommerceCheckout> {
        throw new CommerceError(
            "payment-not-configured",
            "Submit orders through commerce.payment so the configured payment adapter can complete the order.",
        );
    }
    public async preparePayment(
        consents: readonly import("@teqtank/commerce-contracts").CommerceCustomerConsentSelection[] = [],
        customFields: Readonly<Record<string, string>> = {},
        options: CommerceOperationOptions = {},
    ): Promise<SubmitCommerceCheckoutResponse> {
        const response = await this.repository.submit(
            this.currentCheckout?.version,
            consents,
            customFields,
            options,
        );
        this.accept(response.checkout, "calculated");
        return response;
    }
    public completePayment(
        attemptId: string,
        paymentResult: unknown,
        options: CommerceOperationOptions = {},
    ): Promise<CompleteCommerceCheckoutResponse> {
        return this.repository.complete(attemptId, paymentResult, options);
    }
    public get current(): CommerceCheckout | undefined {
        return this.currentCheckout;
    }
    public onChanged(listener: CommerceCheckoutChangedListener): CommerceEventUnsubscribe {
        return this.listeners.subscribe(listener);
    }
    public dispose(): void {
        this.currentCheckout = undefined;
        this.quickBuyActive = false;
        this.listeners.release();
    }
    public invalidate(): void {
        this.currentCheckout = undefined;
        this.quickBuyActive = false;
    }
    private accept(
        checkout: CommerceCheckout,
        reason: CommerceCheckoutChangeReason,
    ): CommerceCheckout {
        if (!this.quickBuyActive) this.cart.adoptCheckoutSnapshot(checkout.cart);
        this.currentCheckout = checkout;
        this.listeners.emit(Object.freeze({ reason, checkout }));
        return checkout;
    }
    private requireCurrent(): CommerceCheckout {
        if (this.currentCheckout === undefined)
            throw new CommerceError("checkout-not-found", "Checkout has not been started.");
        return this.currentCheckout;
    }
}
